import { Maybe } from "@figurepos/fgr-lib-business-logic/lib/common/types"
import * as React from "react"
import { CSSProperties } from "react"
import { last } from "ramda"
import type {
    ParsedTimeSpan,
    TimeRange,
    TimeSpan,
} from "@figurepos/fgr-lib-business-logic/lib/common/dateTime/hours_of_operation_v2"
import { parseTimeSpans } from "@figurepos/fgr-lib-business-logic/lib/common/dateTime/hours_of_operation_v2"
import { Button, Checkbox, Switch } from "antd"
import moment, { Moment } from "moment-timezone"
import TimePickerDeprecated from "../../uiKit/TimePicker/TimePickerDeprecated"
import { MDash, Minus, Plus } from "../../../constants/symbols"
import { getRawTimeSpansFromParsed } from "./utils"

type Props = {
    value: TimeSpan[]
    onChange: (value: TimeSpan[]) => void
}
export class TimePattern2 extends React.PureComponent<Props> {
    static defaultProps = {
        style: {},
    }
    getDaysOfWeek = () => [
        {
            id: "Mon",
            label: "Monday",
        },
        {
            id: "Tue",
            label: "Tuesday",
        },
        {
            id: "Wed",
            label: "Wednesday",
        },
        {
            id: "Thu",
            label: "Thursday",
        },
        {
            id: "Fri",
            label: "Friday",
        },
        {
            id: "Sat",
            label: "Saturday",
        },
        {
            id: "Sun",
            label: "Sunday",
        },
    ]
    addHourToRangeTime = (rangeTime: Maybe<string>): Maybe<string> => {
        return rangeTime && this.convertMomentToRangeTime(this.convertRangeTimeToMoment(rangeTime).add(1, "hour"))
    }
    convertMomentToRangeTime = (time: Moment): string => {
        return time.format("H:mm")
    }
    convertRangeTimeToMoment = (timeString: string): Moment => moment(timeString, "H:mm")
    onDayActiveChanged = (dayId: string, value: boolean) => {
        const currentParsed = parseTimeSpans(this.props.value, false)
        const createNew = value && !currentParsed.find(p => p.dayOfWeek === dayId)
        const newParsed: ParsedTimeSpan[] = createNew
            ? [
                  ...currentParsed,
                  {
                      dayOfWeek: dayId,
                      never: !value,
                      ranges: [
                          {
                              start: "9:00",
                              end: "18:00",
                          },
                      ],
                  },
              ]
            : currentParsed.map(p => {
                  if (p.dayOfWeek === dayId) {
                      const createRanges = value && (!p.ranges || p.ranges.length < 1)
                      return {
                          ...p,
                          ...(createRanges
                              ? {
                                    ranges: [
                                        {
                                            start: "9:00",
                                            end: "18:00",
                                        },
                                    ],
                                }
                              : null),
                          never: !value,
                      }
                  }

                  return p
              })
        this.props.onChange(getRawTimeSpansFromParsed(newParsed))
    }
    onAllDayClicked = (dayId: string, value: boolean) => {
        const newParsed = parseTimeSpans(this.props.value, false).map(p => {
            if (p.dayOfWeek === dayId) {
                return { ...p, always: value }
            }

            return p
        })
        this.props.onChange(getRawTimeSpansFromParsed(newParsed))
    }
    onRangeAdded = (dayId: string) => {
        const newParsed = parseTimeSpans(this.props.value, false).map(p => {
            if (p.dayOfWeek === dayId) {
                const lastRange = p.ranges && last(p.ranges)
                const newRangeStart = lastRange ? this.addHourToRangeTime(lastRange.end) : null
                const newRangeEnd = newRangeStart ? this.addHourToRangeTime(newRangeStart) : null
                return {
                    ...p,
                    ranges: [
                        ...(p.ranges || []),
                        ...[
                            {
                                start: newRangeStart || "9:00",
                                end: newRangeEnd || "10:00",
                            },
                        ],
                    ],
                }
            }

            return p
        })
        this.props.onChange(getRawTimeSpansFromParsed(newParsed))
    }
    onRangeRemoved = (dayId: string, index: number) => {
        const newParsed = parseTimeSpans(this.props.value, false).map(p => {
            if (p.dayOfWeek === dayId) {
                return { ...p, ranges: (p.ranges || []).filter((_, i) => i !== index) }
            }

            return p
        })
        this.props.onChange(getRawTimeSpansFromParsed(newParsed))
    }
    onRangeChange = (rangeId: "start" | "end", rangeIndex: number, dayId: string, value: Moment) => {
        const newParsed = parseTimeSpans(this.props.value, false).map(p => {
            if (p.dayOfWeek === dayId && p.ranges) {
                return {
                    ...p,
                    ranges: p.ranges.map((r, i) => {
                        if (i === rangeIndex) {
                            return { ...r, [rangeId]: this.convertMomentToRangeTime(value) }
                        }

                        return r
                    }),
                }
            }

            return p
        })
        this.props.onChange(getRawTimeSpansFromParsed(newParsed))
    }
    renderRangeRow = (
        range: Maybe<TimeRange>,
        rangeIndex: number,
        showTransparentBtn: boolean,
        dayId: string,
        isActive: boolean,
        allDay: boolean
    ) => {
        return (
            <div
                key={dayId + rangeIndex}
                style={{ ...styles.rangeRow, ...(rangeIndex > 0 ? styles.rangeRowMargin : null) }}>
                <TimePickerDeprecated
                    value={range ? this.convertRangeTimeToMoment(range.start) : null}
                    onChange={(value: Moment) => this.onRangeChange("start", rangeIndex, dayId, value)}
                    placeholder="Start"
                    format="h:mm a"
                    style={styles.rangeInput}
                    disabled={!isActive || allDay}
                />
                <div style={styles.rangesDash}>{MDash}</div>
                <TimePickerDeprecated
                    value={range ? this.convertRangeTimeToMoment(range.end) : null}
                    onChange={(value: Moment) => this.onRangeChange("end", rangeIndex, dayId, value)}
                    placeholder="End"
                    format="h:mm a"
                    style={styles.rangeInput}
                    disabled={!isActive || allDay}
                />
                <div style={styles.allDayContainer}>
                    {rangeIndex === 0 && (
                        <Checkbox
                            disabled={!isActive}
                            onChange={e => this.onAllDayClicked(dayId, e.target.checked)}
                            checked={allDay}
                        />
                    )}
                </div>
                {rangeIndex === 0 ? (
                    <Button
                        type="primary"
                        ghost={showTransparentBtn}
                        onClick={() => this.onRangeAdded(dayId)}
                        disabled={!isActive || allDay}
                        style={styles.addRemoveBtn}>
                        {Plus}
                    </Button>
                ) : (
                    <Button
                        type="danger"
                        ghost={true}
                        disabled={!isActive || allDay}
                        onClick={() => this.onRangeRemoved(dayId, rangeIndex)}
                        style={styles.addRemoveBtn}>
                        {Minus}
                    </Button>
                )}
            </div>
        )
    }
    renderRanges = (ranges: Maybe<TimeRange[]>, dayId: string, isActive: boolean, allDay: boolean) => {
        if (!ranges || ranges.length < 1) {
            return this.renderRangeRow(null, 0, true, dayId, isActive, allDay)
        } else {
            const showTransparentPlusBtn = ranges && ranges.length === 1
            return ranges.map((r, index) =>
                this.renderRangeRow(r, index, showTransparentPlusBtn, dayId, isActive, allDay)
            )
        }
    }

    render() {
        const parsed = parseTimeSpans(this.props.value, false)
        return (
            <div className="time-pattern" style={styles.container}>
                <style jsx={true} global={true}>
                    {`
                        .time-pattern .span-row {
                            margin-bottom: 1rem;
                        }
                        .time-pattern .span-row:nth-last-child(2) {
                            margin-bottom: 0;
                        }
                    `}
                </style>
                {this.getDaysOfWeek().map(day => {
                    const data = parsed.find(p => p.dayOfWeek === day.id)
                    const isActive = !!data && !data.never
                    return (
                        <div key={day.id} style={styles.spanRow} className="span-row">
                            <div style={styles.switchContainer}>
                                <Switch
                                    checked={isActive}
                                    onChange={value => {
                                        this.onDayActiveChanged(day.id, value)
                                    }}
                                />
                            </div>
                            <div style={styles.label}>{day.label}</div>
                            <div style={styles.ranges}>
                                {this.renderRanges(data && data.ranges, day.id, isActive, !!(data && data.always))}
                            </div>
                        </div>
                    )
                })}
                <div style={styles.allDayLabel}>24H</div>
            </div>
        )
    }
}
const styles: Record<string, CSSProperties> = {
    container: {
        position: "relative",
    },
    spanRow: {
        display: "flex",
    },
    switchContainer: {
        flex: "0 0 3.75rem",
        paddingTop: "5px",
    },
    label: {
        flex: "0 0 7rem",
        paddingTop: "6px",
        fontWeight: "bold",
        fontSize: "14px",
    },
    ranges: {},
    rangeRow: {
        display: "flex",
    },
    rangeInput: {
        width: "100%",
    },
    rangesDash: {
        padding: "5px 0.5rem 0",
    },
    addRemoveBtn: {
        flex: 0,
    },
    rangeRowMargin: {
        marginTop: "0.5rem",
    },
    allDayContainer: {
        flex: "0 0 40px",
        paddingTop: "5px",
        textAlign: "center",
    },
    allDayLabel: {
        position: "absolute",
        top: "-15px",
        right: "49px",
        fontSize: "11px",
        opacity: 0.8,
    },
}
